// Cloud Sync Service - UNIFIED APPROACH
// Handles ALL localStorage patterns in the app
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  collection,
  getDocs,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import firebaseAuthService from './firebaseAuthService';
import { contentKey } from './contentKey';

class CloudSyncService {
  constructor() {
    this.db = getFirestore();
    this.isOnline = navigator.onLine;
    this.autoSyncInterval = null;
    this.syncInProgress = false;
    this.lastSyncTime = null;
    this.realtimeUnsubscribes = []; // Firestore onSnapshot unsubscribes

    // Listen for online/offline changes
    window.addEventListener('online', () => {
      this.isOnline = true;
      console.log('🌐 Back online - resuming sync');
      this.startAutoSync();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      console.log('📱 Gone offline - pausing sync');
      this.stopAutoSync();
    });

    // Start auto-sync when initialized
    this.startAutoSync();
  }

  // ==================== LOGIN SYNC - DOWNLOAD EVERYTHING ====================

  /**
   * Download ALL data from Firebase on login
   * This ensures user always has the latest data when they log in
   * ENHANCED: Check both syncData and farmData collections
   */
  async downloadAllDataOnLogin(farmId, silent = false) {
    if (!farmId) {
      console.error('❌ No farmId provided for login sync');
      return false;
    }

    try {
      let updatedCount = 0;

      const [syncDocSnap, farmDocSnap] = await Promise.all([
        getDoc(doc(this.db, 'syncData', farmId)),
        getDoc(doc(this.db, 'farmData', farmId)),
      ]);

      if (!syncDocSnap.exists() && !farmDocSnap.exists()) {
        return true; // Not an error, just no data yet
      }

      // Every page saves to farmData, some only there, so it always wins. syncData only fills
      // in data types farmData doesn't have: its copies can be stale, and timestamps can't
      // be trusted because older versions of the app re-stamped them with the current time.
      const incoming = new Map();
      const offer = (key, data, lastModified) => {
        if (data === undefined || data === null) return;
        incoming.set(key, { data, stamp: lastModified || '' });
      };

      if (syncDocSnap.exists()) {
        Object.entries(syncDocSnap.data().farmData || {}).forEach(([key, info]) => {
          if (key === 'lastModified') return;
          offer(key, info?.actualData || info?.data || info, info?.lastModified);
        });
      }
      if (farmDocSnap.exists()) {
        Object.entries(farmDocSnap.data()).forEach(([dataType, info]) => {
          if (dataType === 'lastModified') return;
          offer(this.getLocalStorageKeyForDataType(dataType, farmId), info?.data || info, info?.lastModified);
        });
      }

      incoming.forEach(({ data, stamp }, key) => {
        try {
          if (contentKey(data) === contentKey(this.extractDataFromLocalStorage(key))) return;
          if (this.hasNewerUnsyncedChanges(key, stamp)) return;
          this.saveDataToLocalStorage(key, data);
          updatedCount++;
        } catch (error) {
          console.error(`❌ Error processing ${key} from Firebase:`, error);
        }
      });

      if (!silent) {
        console.log(`🎉 Download complete. Updated ${updatedCount} data types`);
      }

      // Pages only need to reload when something actually arrived.
      if (updatedCount > 0 || !silent) this.notifyDataChanged({ farmId, updatedCount, source: 'forceDownload' });

      return true;
    } catch (error) {
      if (!silent) {
        console.error('❌ Force download failed:', error);
      }
      return false;
    }
  }

  /**
   * Is there an edit on this device, not yet uploaded, that is newer than the cloud copy?
   */
  hasNewerUnsyncedChanges(key, cloudStamp) {
    try {
      const local = JSON.parse(localStorage.getItem(key));
      return Boolean(local && local.synced === false && (local.lastModified || '') > (cloudStamp || ''));
    } catch {
      return false;
    }
  }

  notifyDataChanged(detail) {
    [
      'parcelDataUpdated',
      'inventoryDataUpdated',
      'stockAdjustmentDataUpdated',
      'seasonDataUpdated',
      'companySettingsUpdated',
      'tasksDataUpdated',
      'fieldsDataUpdated',
      'weatherDataUpdated',
      'financesDataUpdated',
      'workPlansDataUpdated',
      'settingsDataUpdated',
      'forceDownloadComplete',
      'cloudSyncComplete',
    ].forEach((eventName) => window.dispatchEvent(new CustomEvent(eventName, { detail })));
  }

  /**
   * Runs one download at a time. A change arriving during a download runs one more
   * afterwards, so the last change is never missed.
   */
  async queueDownload(farmId) {
    if (this.downloadRunning) { this.downloadAgain = true; return; }
    this.downloadRunning = true;
    try {
      do {
        this.downloadAgain = false;
        await this.downloadAllDataOnLogin(farmId, true);
      } while (this.downloadAgain);
    } finally {
      this.downloadRunning = false;
    }
  }

  /**
   * Get localStorage key for a data type
   */
  getLocalStorageKeyForDataType(dataType, farmId) {
    const keyMappings = {
      'parcels': `parcelsData_${farmId}`,
      'parcelsData': `parcelsData_${farmId}`,
      'inventory': `inventoryData_${farmId}`,
      'inventoryData': `inventoryData_${farmId}`,
      'stockAdjustment': `stockAdjustmentData_${farmId}`,
      'stockAdjustmentData': `stockAdjustmentData_${farmId}`,
      'seasons': `farmSeasons_${farmId}`,
      'farmSeasons': `farmSeasons_${farmId}`,
      'companySettings': `companySettings_${farmId}`,
      'users': `usersData_${farmId}`,
      'usersData': `usersData_${farmId}`,
      'tasks': `tasksData_${farmId}`,
      'tasksData': `tasksData_${farmId}`,
      'finances': `financesData_${farmId}`,
      'financesData': `financesData_${farmId}`,
      'fields': `fieldsData_${farmId}`,
      'fieldsData': `fieldsData_${farmId}`,
      'workPlans': `workPlansData_${farmId}`,
      'workPlansData': `workPlansData_${farmId}`,
      'weather': `weatherData_${farmId}`,
      'weatherData': `weatherData_${farmId}`,
      'contracts': `contractsData_${farmId}`,
      'contractsData': `contractsData_${farmId}`,
      'motorinData': `motorinData_${farmId}`,
      'utilajeData': `utilajeData_${farmId}`,
      'facturiData': `facturiData_${farmId}`,
      'produseData': `produseData_${farmId}`,
      'lucrariData': `lucrariData_${farmId}`
    };
    
    return keyMappings[dataType] || `${dataType}_${farmId}`;
  }

  // ==================== UNIFIED SYNC ENGINE ====================

  /**
   * Get current farm ID from session
   */
  getCurrentFarmId() {
    const session = firebaseAuthService.getCurrentSession();
    return session?.farm?.id || null;
  }

  /**
   * Extract actual data from localStorage (handles both wrapped and direct data)
   */
  extractDataFromLocalStorage(key) {
    try {
      const rawData = localStorage.getItem(key);
      if (!rawData) return null;
      
      const parsed = JSON.parse(rawData);
      
      // Check if data is wrapped by universalDataService
      if (parsed && typeof parsed === 'object' && parsed.hasOwnProperty('data')) {
        return parsed.data; // Extract the actual data
      }
      
      // Direct data (not wrapped)
      return parsed;
    } catch (error) {
      console.error(`❌ Error extracting data from ${key}:`, error);
      return null;
    }
  }

  /**
   * Save data to localStorage (preserving wrapper structure if it exists)
   */
  saveDataToLocalStorage(key, data) {
    try {
      const existingRaw = localStorage.getItem(key);
      
      if (existingRaw) {
        const existingParsed = JSON.parse(existingRaw);
        
        // If existing data is wrapped, preserve the wrapper
        if (existingParsed && typeof existingParsed === 'object' && existingParsed.hasOwnProperty('data')) {
          const wrappedData = {
            ...existingParsed,
            data: data, // Update the actual data
            lastModified: new Date().toISOString(),
            synced: true // Mark as synced since we're updating from cloud
          };
          localStorage.setItem(key, JSON.stringify(wrappedData));
          
          // CRITICAL: Force UI refresh for this specific data type
          this.forceUIRefreshForKey(key, data);
          return;
        }
      }
      
      // Save as direct data
      localStorage.setItem(key, JSON.stringify(data));
      console.log(`📄 Saved direct data to ${key}:`, {
        dataLength: Array.isArray(data) ? data.length : 'object'
      });
      
      // CRITICAL: Force UI refresh for this specific data type
      this.forceUIRefreshForKey(key, data);
      
    } catch (error) {
      console.error(`❌ Error saving data to ${key}:`, error);
    }
  }

  /**
   * Force UI refresh for specific data types
   */
  forceUIRefreshForKey(key, data) {
    
    // Trigger specific events based on key
    if (key.includes('parcelsData')) {
      // Multiple approaches to ensure ParcelManager updates
      console.log('📱 Triggering multiple ParcelManager refresh methods...');
      
      // Method 1: Custom event
      window.dispatchEvent(new CustomEvent('parcelDataUpdated', { detail: data }));
      
      // Method 2: Storage event simulation
      window.dispatchEvent(new CustomEvent('storage', { 
        detail: { key, newValue: JSON.stringify(data) } 
      }));
      
      // Method 3: Force immediate re-render by updating a timestamp
      localStorage.setItem('lastParcelUpdate', Date.now().toString());
      
      // Method 4: Dispatch a more generic update event
      window.dispatchEvent(new CustomEvent('dataUpdated', { 
        detail: { type: 'parcels', data, timestamp: Date.now() } 
      }));
      
      console.log('📱 All ParcelManager refresh methods triggered');
    }
    
    if (key.includes('inventoryData')) {
      window.dispatchEvent(new CustomEvent('inventoryDataUpdated', { detail: data }));
      console.log('📱 Triggered inventoryDataUpdated event');
    }
    if (key.includes('stockAdjustmentData')) {
      window.dispatchEvent(new CustomEvent('stockAdjustmentDataUpdated', { detail: data }));
      console.log('📱 Triggered stockAdjustmentDataUpdated event');
    }
    if (key.includes('farmSeasons')) {
      window.dispatchEvent(new CustomEvent('seasonDataUpdated', { detail: data }));
      console.log('📱 Triggered seasonDataUpdated event');
    }
    if (key.includes('companySettings')) {
      window.dispatchEvent(new CustomEvent('companySettingsUpdated', { detail: data }));
      console.log('📱 Triggered companySettingsUpdated event');
    }
    
    // Force a general refresh
    window.dispatchEvent(new CustomEvent('storageChanged', { detail: { key, data } }));
    
  }

  /**
   * DISCOVER ALL FARM DATA - Finds ALL data for current farm
   */
  discoverAllFarmData(farmId) {
    const allKeys = Object.keys(localStorage);
    const farmKeys = allKeys.filter(key => key.includes(farmId));
    
    const farmData = {};
    
    farmKeys.forEach(key => {
      const data = this.extractDataFromLocalStorage(key);
      if (data !== null) {
        // Determine data type from key
        const dataType = this.getDataTypeFromKey(key, farmId);
        farmData[dataType] = {
          key: key,
          data: data,
          size: JSON.stringify(data).length
        };
      }
    });

    console.log(`🔍 Discovered ${Object.keys(farmData).length} data types for farm ${farmId}:`, Object.keys(farmData));
    return farmData;
  }

  /**
   * Extract data type from localStorage key
   */
  getDataTypeFromKey(key, farmId) {
    // Remove farmId from key to get data type
    const withoutFarmId = key.replace(`_${farmId}`, '').replace(`${farmId}_`, '');
    
    // Handle special cases
    if (key.includes('companySettings')) return 'companySettings';
    if (key.includes('farmSeasons')) return 'farmSeasons';
    if (key.includes('farmUsers')) return 'farmUsers';
    if (key.includes('tParcelGroups')) return 'tParcelGroups';
    if (key.includes('weatherHistory')) return 'weatherHistory';
    if (key.includes('agrimanage-')) return withoutFarmId.replace('agrimanage-', '');
    
    return withoutFarmId;
  }

  /**
   * ULTIMATE SYNC METHOD - BULLETPROOF & PERFECT
   * - Companies/locations ALWAYS accumulate (never shrink)
   * - ALL changes have timestamps - newest wins conflicts
   * - ZERO data loss guaranteed
   */
  async performCompleteSync() {
    if (this.syncInProgress) {
      console.log('⏳ Sync already in progress');
      return { uploaded: 0, downloaded: 0, errors: [] };
    }

    this.syncInProgress = true;
    const farmId = this.getCurrentFarmId();
    
    if (!farmId) {
      this.syncInProgress = false;
      throw new Error('No farm ID found');
    }

    console.log(`🚀 STARTING ULTIMATE SYNC for farm: ${farmId}`);
    
    let uploaded = 0;
    let downloaded = 0;

    try {
      // STEP 1: LOAD CURRENT CLOUD DATA (MASTER TRUTH)
      console.log('☁️ LOADING CLOUD MASTER DATA...');
      
      const docRef = doc(this.db, 'syncData', farmId);
      const docSnap = await getDoc(docRef);
      
      let cloudData = {};
      if (docSnap.exists()) {
        cloudData = docSnap.data().farmData || {};
        console.log(`☁️ Found cloud data for ${Object.keys(cloudData).length} data types`);
      } else {
        console.log('☁️ No cloud data - starting fresh');
      }

      // STEP 2: ULTIMATE MERGE WITH TIMESTAMP CONFLICT RESOLUTION
      console.log('🔥 ULTIMATE MERGE WITH TIMESTAMP RESOLUTION...');
      
      let hasChanges = false;
      const currentTimestamp = new Date().toISOString();
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes(farmId)) {
          console.log(`📦 ULTIMATE PROCESSING: ${key}`);
          const rawValue = localStorage.getItem(key);
          
          try {
            const parsedValue = JSON.parse(rawValue);
            
            // Extract local data with timestamp
            let localData = null;
            let localTimestamp = currentTimestamp;
            
            if (parsedValue && typeof parsedValue === 'object' && parsedValue.hasOwnProperty('data')) {
              localData = parsedValue.data;
              localTimestamp = parsedValue.lastModified || currentTimestamp;
            } else if (Array.isArray(parsedValue) || (parsedValue && typeof parsedValue === 'object')) {
              localData = parsedValue;
            }
            
            if (localData !== null) {
              const existingCloudData = cloudData[key]?.actualData;
              const cloudTimestamp = cloudData[key]?.lastModified || '1970-01-01T00:00:00.000Z';
              
              let ultimateData = localData;
              
              if (existingCloudData) {
                // ULTIMATE MERGE LOGIC
                if (Array.isArray(localData) && Array.isArray(existingCloudData)) {
                  console.log(`🔥 ULTIMATE ARRAY MERGE for ${key}`);
                  
                  // Create master array with ALL items
                  const masterArray = [];
                  const seenIds = new Set();
                  
                  // Process cloud items first
                  existingCloudData.forEach(cloudItem => {
                    const itemId = cloudItem.id || cloudItem.cnp || JSON.stringify(cloudItem);
                    const itemTimestamp = cloudItem.lastModified || cloudTimestamp;
                    
                    masterArray.push({
                      ...cloudItem,
                      lastModified: itemTimestamp,
                      source: 'cloud'
                    });
                    seenIds.add(itemId);
                  });
                  
                  // Process local items - merge or add
                  localData.forEach(localItem => {
                    const itemId = localItem.id || localItem.cnp || JSON.stringify(localItem);
                    const itemTimestamp = localItem.lastModified || localTimestamp;
                    
                    const existingIndex = masterArray.findIndex(item => {
                      const existingId = item.id || item.cnp || JSON.stringify({...item, lastModified: undefined, source: undefined});
                      return existingId === itemId;
                    });
                    
                    if (existingIndex >= 0) {
                      // CONFLICT RESOLUTION: Newest timestamp wins
                      const existingTimestamp = masterArray[existingIndex].lastModified;
                      if (itemTimestamp > existingTimestamp) {
                        console.log(`🏆 LOCAL WINS: ${itemId} (${itemTimestamp} > ${existingTimestamp})`);
                        masterArray[existingIndex] = {
                          ...localItem,
                          lastModified: itemTimestamp,
                          source: 'local-winner'
                        };
                      } else {
                        console.log(`☁️ CLOUD WINS: ${itemId} (${existingTimestamp} >= ${itemTimestamp})`);
                      }
                    } else {
                      // NEW ITEM - add to master
                      console.log(`➕ NEW ITEM: ${itemId}`);
                      masterArray.push({
                        ...localItem,
                        lastModified: itemTimestamp,
                        source: 'local-new'
                      });
                    }
                  });
                  
                  // Clean up metadata
                  ultimateData = masterArray.map(item => {
                    const { source, ...cleanItem } = item;
                    return cleanItem;
                  });
                  
                  console.log(`🔥 ULTIMATE RESULT: ${existingCloudData.length} cloud + ${localData.length} local = ${ultimateData.length} final`);
                  
                } else if (typeof localData === 'object' && typeof existingCloudData === 'object') {
                  console.log(`🔥 ULTIMATE OBJECT MERGE for ${key}`);
                  
                  if (key.includes('companySettings')) {
                    // ULTIMATE SETTINGS MERGE - ALWAYS ACCUMULATE
                    const cloudCompanies = existingCloudData.companies || [];
                    const localCompanies = localData.companies || [];
                    const cloudLocations = existingCloudData.locations || [];
                    const localLocations = localData.locations || [];
                    
                    console.log(`🔍 SETTINGS DEBUG for ${key}:`);
                    console.log(`  ☁️ Cloud companies:`, cloudCompanies);
                    console.log(`  💻 Local companies:`, localCompanies);
                    console.log(`  ☁️ Cloud locations:`, cloudLocations);
                    console.log(`  💻 Local locations:`, localLocations);
                    
                    // BULLETPROOF ACCUMULATION - Never lose data
                    let ultimateCompanies = [];
                    let ultimateLocations = [];
                    
                    // If either cloud or local has data, preserve ALL of it
                    if (cloudCompanies.length > 0 || localCompanies.length > 0) {
                      ultimateCompanies = [...new Set([...cloudCompanies, ...localCompanies])];
                    }
                    
                    if (cloudLocations.length > 0 || localLocations.length > 0) {
                      ultimateLocations = [...new Set([...cloudLocations, ...localLocations])];
                    }
                    
                    // SAFETY CHECK - Never allow empty arrays if we had data before
                    if (ultimateCompanies.length === 0 && (cloudCompanies.length > 0 || localCompanies.length > 0)) {
                      console.log('🚨 PREVENTING COMPANY DATA LOSS!');
                      ultimateCompanies = cloudCompanies.length > 0 ? cloudCompanies : localCompanies;
                    }
                    
                    if (ultimateLocations.length === 0 && (cloudLocations.length > 0 || localLocations.length > 0)) {
                      console.log('🚨 PREVENTING LOCATION DATA LOSS!');
                      ultimateLocations = cloudLocations.length > 0 ? cloudLocations : localLocations;
                    }
                    
                    ultimateData = {
                      ...existingCloudData,
                      ...localData,
                      companies: ultimateCompanies,
                      locations: ultimateLocations,
                      lastModified: Math.max(localTimestamp, cloudTimestamp) === localTimestamp ? localTimestamp : cloudTimestamp
                    };
                    
                    console.log(`🔥 ULTIMATE SETTINGS RESULT:`);
                    console.log(`  📈 Companies: ${cloudCompanies.length}+${localCompanies.length}=${ultimateCompanies.length}`);
                    console.log(`  📈 Locations: ${cloudLocations.length}+${localLocations.length}=${ultimateLocations.length}`);
                    console.log(`  🏆 Final companies:`, ultimateCompanies);
                    console.log(`  🏆 Final locations:`, ultimateLocations);
                  } else {
                    // TIMESTAMP-BASED OBJECT MERGE
                    if (localTimestamp > cloudTimestamp) {
                      ultimateData = { ...existingCloudData, ...localData, lastModified: localTimestamp };
                      console.log(`🏆 LOCAL OBJECT WINS: ${key} (${localTimestamp} > ${cloudTimestamp})`);
                    } else {
                      ultimateData = { ...localData, ...existingCloudData, lastModified: cloudTimestamp };
                      console.log(`☁️ CLOUD OBJECT WINS: ${key} (${cloudTimestamp} >= ${localTimestamp})`);
                    }
                  }
                }
              } else {
                console.log(`➕ NEW DATA TYPE: ${key}`);
                ultimateData = { ...localData, lastModified: localTimestamp };
              }
              
              cloudData[key] = {
                actualData: ultimateData,
                originalFormat: 'wrapped',
                // Keep the real time when nothing changed, so old data never looks new.
                lastModified: contentKey(ultimateData) === contentKey(existingCloudData) ? cloudTimestamp : currentTimestamp,
                dataType: key.split('_')[0]
              };
              
              hasChanges = true;
            }
          } catch (e) {
            console.log(`⚠️ Parse error ${key}:`, e);
          }
        }
      }
      
      // STEP 3: SAVE ULTIMATE DATA TO CLOUD
      if (hasChanges) {
        console.log('☁️ SAVING ULTIMATE DATA TO CLOUD...');
        await setDoc(docRef, {
          farmData: cloudData,
          lastUpdated: currentTimestamp,
          farmId: farmId,
          syncVersion: '4.0-ultimate'
        });
        uploaded = Object.keys(cloudData).length;
        console.log(`✅ ULTIMATE UPLOAD: ${uploaded} data types`);
      }

      // STEP 4: DOWNLOAD ULTIMATE DATA TO LOCAL
      console.log('📥 DOWNLOADING ULTIMATE DATA...');
      
      for (const [key, dataInfo] of Object.entries(cloudData)) {
        const ultimateData = dataInfo.actualData;
        
        // Save in universalDataService format with timestamp
        const restoredData = {
          data: ultimateData,
          lastModified: currentTimestamp,
          synced: true,
          farmId: farmId,
          dataType: dataInfo.dataType || key.split('_')[0]
        };
        
        localStorage.setItem(key, JSON.stringify(restoredData));
        downloaded++;
        
        console.log(`📥 ULTIMATE DOWNLOAD ${key}:`, Array.isArray(ultimateData) ? `${ultimateData.length} items` : 'object');
      }

      // STEP 5: TRIGGER ULTIMATE UI REFRESH
      console.log('🔄 ULTIMATE UI REFRESH...');
      
      const refreshEvents = [
        'parcelDataUpdated',
        'inventoryDataUpdated', 
        'stockAdjustmentDataUpdated',
        'seasonDataUpdated',
        'companySettingsUpdated',
        'tasksDataUpdated',
        'fieldsDataUpdated',
        'weatherDataUpdated',
        'financesDataUpdated',
        'workPlansDataUpdated',
        'settingsDataUpdated',
        'cloudSyncComplete'
      ];
      
      refreshEvents.forEach(eventName => {
        window.dispatchEvent(new CustomEvent(eventName, { detail: 'ULTIMATE SYNC COMPLETE' }));
      });
      
      console.log('🚀 ULTIMATE SYNC COMPLETE - BULLETPROOF!');
      console.log(`🎉 ULTIMATE RESULT: ⬆️${uploaded} ⬇️${downloaded}`);

      return { uploaded, downloaded, errors: [] };

    } catch (error) {
      console.error('❌ SYNC ERROR:', error);
      throw error;
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Every minute: fetch what's new, then upload edits that haven't reached the cloud yet.
   * It doesn't merge lists: a merge would bring back items deleted on another device.
   */
  startAutoSync() {
    if (this.autoSyncInterval) return;
    
    this.autoSyncInterval = setInterval(async () => {
      if (this.isOnline && !this.syncInProgress) {
        try {
          await this.uploadUnsyncedChanges();
        } catch (error) {
          console.error('❌ Auto-sync failed:', error);
        }
      }
    }, 60 * 1000);
  }

  async uploadUnsyncedChanges() {
    const farmId = this.getCurrentFarmId();
    if (!farmId) return;

    // Download first: a local edit older than the cloud copy gets replaced, not uploaded.
    await this.queueDownload(farmId);

    const { default: universalDataService } = await import('./universalDataService');
    const suffix = `_${farmId}`;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.endsWith(suffix)) continue;
      let wrapper = null;
      try { wrapper = JSON.parse(localStorage.getItem(key)); } catch { continue; }
      if (!wrapper || wrapper.synced !== false || wrapper.data === undefined) continue;

      await universalDataService.syncToCloud(farmId, key.slice(0, -suffix.length), wrapper.data);
      // Re-read: the page may have saved something newer during the upload.
      const latest = JSON.parse(localStorage.getItem(key));
      if (latest && latest.lastModified === wrapper.lastModified) {
        localStorage.setItem(key, JSON.stringify({ ...latest, synced: true, lastSynced: new Date().toISOString() }));
      }
    }
  }

  stopAutoSync() {
    if (this.autoSyncInterval) {
      clearInterval(this.autoSyncInterval);
      this.autoSyncInterval = null;
      console.log('⏹️ Auto-sync stopped');
    }
  }

  getSyncStatus() {
    return {
      isOnline: this.isOnline,
      syncInProgress: this.syncInProgress,
      lastSyncTimestamp: this.lastSyncTime,
      autoSyncEnabled: !!this.autoSyncInterval
    };
  }

  debugListLocalStorageKeys() {
    if (process.env.NODE_ENV === 'production') return;
    const farmId = this.getCurrentFarmId();
    if (!farmId) {
      console.log('❌ No current farm ID found');
      return;
    }
    
    console.log(`🔍 DEBUG: All localStorage keys for farm ${farmId}:`);
    const farmData = this.discoverAllFarmData(farmId);
    
    console.log('📋 Discovered farm data:');
    Object.entries(farmData).forEach(([dataType, info]) => {
      console.log(`  📝 ${dataType}: ${info.key} (${info.size} bytes)`);
    });
  }

  /**
   * Trigger UI refresh after sync
   */
  triggerUIRefresh() {
    // Trigger specific component updates
    window.dispatchEvent(new CustomEvent('parcelDataUpdated', { detail: 'Cloud sync completed' }));
    window.dispatchEvent(new CustomEvent('inventoryDataUpdated', { detail: 'Cloud sync completed' }));
    window.dispatchEvent(new CustomEvent('stockAdjustmentDataUpdated', { detail: 'Cloud sync completed' }));
    window.dispatchEvent(new CustomEvent('seasonDataUpdated', { detail: 'Cloud sync completed' }));
    window.dispatchEvent(new CustomEvent('companySettingsUpdated', { detail: 'Cloud sync completed' }));
    window.dispatchEvent(new CustomEvent('cloudSyncComplete', { detail: 'All data synced' }));
    
    console.log('📱 Triggered UI refresh events');
  }

  /**
   * Verify if data exists on Firebase for this farm
   */
  async verifyFirebaseData(farmId) {
    try {
      const docRef = doc(this.db, 'syncData', farmId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const cloudData = docSnap.data();
        const farmData = cloudData.farmData || {};
        const dataTypes = Object.keys(farmData);
        
        console.log(`🔥 FIREBASE VERIFICATION: Found ${dataTypes.length} data types`);
        console.log(`🔥 Data types: ${dataTypes.join(', ')}`);
        
        return dataTypes.length > 0;
      } else {
        console.log('🔥 FIREBASE VERIFICATION: No data found');
        return false;
      }
    } catch (error) {
      console.error('❌ Firebase verification error:', error);
      return false;
    }
  }

  /**
   * Sync all data (alias for performCompleteSync for compatibility)
   */
  async syncAllData(farmId) {
    try {
      console.log('🚀 SYNC ALL DATA STARTING...');
      const result = await this.performCompleteSync();
      console.log('🚀 SYNC ALL DATA COMPLETE:', result);
      return true;
    } catch (error) {
      console.error('❌ Sync all data error:', error);
      return false;
    }
  }

  // Legacy compatibility
  async manualSync() { return await this.performCompleteSync(); }
  async testSync() { 
    try {
      const result = await this.performCompleteSync();
      return { success: true, message: `Synced ${result.uploaded} up, ${result.downloaded} down` };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
  async syncSpecificData(dataType) { return await this.performCompleteSync(); }

  /**
   * Debug function to diagnose sync issues
   */
  debugSyncIssues() {
    if (process.env.NODE_ENV === 'production') return;
    const farmId = this.getCurrentFarmId();
    if (!farmId) {
      console.log('❌ No farm ID found');
      return;
    }
    
    console.log('🔍 SYNC DEBUG REPORT:');
    console.log('==================');
    
    // Check parcelsData specifically
    const parcelsKey = `parcelsData_${farmId}`;
    const parcelsRaw = localStorage.getItem(parcelsKey);
    
    if (parcelsRaw) {
      try {
        const parcelsData = JSON.parse(parcelsRaw);
        console.log(`📦 Raw parcelsData from localStorage:`, parcelsData);
        
        if (parcelsData.data) {
          console.log(`📄 Wrapped parcelsData.data:`, parcelsData.data);
          console.log(`📊 Parcels count: ${Array.isArray(parcelsData.data) ? parcelsData.data.length : 'not array'}`);
        } else {
          console.log(`📄 Direct parcelsData:`, parcelsData);
          console.log(`📊 Parcels count: ${Array.isArray(parcelsData) ? parcelsData.length : 'not array'}`);
        }
      } catch (error) {
        console.error('❌ Error parsing parcelsData:', error);
      }
    } else {
      console.log('❌ No parcelsData found in localStorage');
    }
    
    // Test what universalDataService would return
    console.log('🧪 Testing universalDataService.loadParcels():');
    import('../services/universalDataService').then(({ default: universalDataService }) => {
      universalDataService.loadParcels().then(result => {
        console.log('📤 universalDataService.loadParcels() returns:', result);
        console.log(`📊 Count: ${Array.isArray(result) ? result.length : 'not array'}`);
      }).catch(error => {
        console.error('❌ universalDataService.loadParcels() error:', error);
      });
    });
    
    console.log('==================');
  }

  /**
   * Debug what's actually in the cloud database
   */
  async debugCloudData() {
    if (process.env.NODE_ENV === 'production') return;
    const farmId = this.getCurrentFarmId();
    if (!farmId) {
      console.log('❌ No farm ID found for debug');
      return;
    }
    
    console.log(`🔍 DEBUGGING CLOUD DATA for farm: ${farmId}`);
    
    try {
      const docRef = doc(this.db, 'syncData', farmId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const cloudData = docSnap.data();
        console.log(`☁️ CLOUD DATA EXISTS:`, cloudData);
        console.log(`☁️ Farm data keys:`, Object.keys(cloudData.farmData || {}));
        
        if (cloudData.farmData) {
          for (const [key, dataInfo] of Object.entries(cloudData.farmData)) {
            console.log(`☁️ ${key}:`, {
              dataType: dataInfo.dataType,
              originalFormat: dataInfo.originalFormat,
              dataSize: Array.isArray(dataInfo.actualData) ? dataInfo.actualData.length : 'object',
              actualData: dataInfo.actualData
            });
          }
        }
      } else {
        console.log('❌ NO CLOUD DATA FOUND');
      }
      
      // Also check what's in localStorage
      console.log(`💾 LOCAL STORAGE CHECK:`);
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes(farmId)) {
          const value = localStorage.getItem(key);
          try {
            const parsed = JSON.parse(value);
            console.log(`💾 ${key}:`, {
              hasData: !!parsed.data,
              dataSize: Array.isArray(parsed.data) ? parsed.data.length : typeof parsed.data,
              actualData: parsed.data
            });
          } catch (e) {
            console.log(`💾 ${key}: Could not parse`);
          }
        }
      }
      
    } catch (error) {
      console.error('❌ Debug error:', error);
    }
  }

  /**
   * Setup real-time sync using Firestore onSnapshot.
   * Any change on any device fires instantly on all other devices.
   * Falls back to 5-second polling if the listener fails.
   */
  setupInstantRefreshSync(farmId) {
    this.stopInstantRefreshSync();
    this.realtimeFarmId = farmId;

    // Watch both documents: some pages save only to farmData.
    this.realtimeUnsubscribes = ['syncData', 'farmData'].map((collectionName) => {
      let isFirstSnapshot = true;
      return onSnapshot(
        doc(this.db, collectionName, farmId),
        (snapshot) => {
          // First snapshot: login already downloaded everything.
          // Pending writes: this device's own save, already applied locally.
          if (isFirstSnapshot) { isFirstSnapshot = false; return; }
          if (snapshot.metadata.hasPendingWrites) return;
          this.queueDownload(farmId);
        },
        (error) => {
          console.error(`❌ Real-time listener failed on ${collectionName}, falling back to polling:`, error);
          if (this.instantRefreshInterval) return;
          this.instantRefreshInterval = setInterval(() => {
            if (this.isOnline) this.queueDownload(farmId);
          }, 15000);
        }
      );
    });

    // Catch up when returning to the app or coming back online. Installed once only.
    if (!this.catchUpInstalled) {
      this.catchUpInstalled = true;
      const catchUp = () => {
        if (this.realtimeFarmId && navigator.onLine !== false && !document.hidden) this.queueDownload(this.realtimeFarmId);
      };
      window.addEventListener('focus', catchUp);
      window.addEventListener('online', catchUp);
      document.addEventListener('visibilitychange', catchUp);

      // Another tab of this browser shares this device's storage, so tell the pages here.
      let storageTimer = null;
      window.addEventListener('storage', (event) => {
        if (!this.realtimeFarmId || !event.key || !event.key.includes(this.realtimeFarmId)) return;
        clearTimeout(storageTimer);
        storageTimer = setTimeout(() => this.notifyDataChanged({ farmId: this.realtimeFarmId, source: 'otherTab' }), 100);
      });
    }

    console.log('✅ REAL-TIME sync active — changes appear instantly on all devices');
  }

  /**
   * Stop instant refresh sync
   */
  stopInstantRefreshSync() {
    (this.realtimeUnsubscribes || []).forEach((unsubscribe) => unsubscribe());
    this.realtimeUnsubscribes = [];
    this.realtimeFarmId = null;
    if (this.instantRefreshInterval) {
      clearInterval(this.instantRefreshInterval);
      this.instantRefreshInterval = null;
    }
  }

  /**
   * Start real-time auto-sync system
   * This will automatically sync data to Firebase when changes are detected
   * and periodically check for updates from other devices
   */
  startRealTimeSync() {
    console.log('🔄 Starting REAL-TIME SYNC system...');
    
    // Stop any existing intervals
    this.stopRealTimeSync();
    
    // 1. Listen for localStorage changes and auto-sync to Firebase
    this.setupAutoUploadOnChanges();
    
    // 2. Periodically check Firebase for updates from other devices
    this.setupAutoDownloadCheck();
    
    // 3. Listen for online/offline status
    this.setupOnlineStatusMonitoring();
    
    console.log('✅ REAL-TIME SYNC system started!');
  }

  /**
   * Setup automatic upload when local data changes
   */
  setupAutoUploadOnChanges() {
    // Listen for storage events (when data changes)
    window.addEventListener('storage', (e) => {
      if (e.key && e.key.includes('_farm_') && e.newValue) {
        console.log('📤 Auto-upload triggered by storage change:', e.key);
        this.debounceAutoSync();
      }
    });

    // Listen for custom data update events
    const dataUpdateEvents = [
      'parcelDataUpdated',
      'inventoryDataUpdated', 
      'stockAdjustmentDataUpdated',
      'companySettingsUpdated',
      'seasonDataUpdated'
    ];

    dataUpdateEvents.forEach(eventName => {
      window.addEventListener(eventName, () => {
        console.log('📤 Auto-upload triggered by event:', eventName);
        this.debounceAutoSync();
      });
    });
  }

  /**
   * Setup periodic check for updates from Firebase
   */
  setupAutoDownloadCheck() {
    // Check for updates every 30 seconds
    this.autoDownloadInterval = setInterval(async () => {
      if (this.isOnline && !this.syncInProgress) {
        console.log('📥 Auto-checking Firebase for updates...');
        try {
          await this.checkForFirebaseUpdates();
        } catch (error) {
          console.error('❌ Auto-download check failed:', error);
        }
      }
    }, 30000); // 30 seconds
  }

  /**
   * Check if Firebase has newer data and download if needed
   */
  async checkForFirebaseUpdates() {
    const farmId = this.getCurrentFarmId();
    if (!farmId) return;

    try {
      const docRef = doc(this.db, 'syncData', farmId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const cloudData = docSnap.data();
        const cloudLastUpdated = cloudData.lastUpdated;
        
        // Check if cloud data is newer than our last sync
        if (cloudLastUpdated && cloudLastUpdated > this.lastSyncTime) {
          console.log('📥 Newer data found on Firebase, downloading...');
          await this.downloadAllDataOnLogin(farmId);
          this.lastSyncTime = cloudLastUpdated;
        }
      }
    } catch (error) {
      console.error('❌ Error checking Firebase updates:', error);
    }
  }

  /**
   * Debounced auto-sync to prevent too many rapid syncs
   */
  debounceAutoSync() {
    // Clear existing timeout
    if (this.autoSyncTimeout) {
      clearTimeout(this.autoSyncTimeout);
    }

    // Set new timeout for 3 seconds
    this.autoSyncTimeout = setTimeout(async () => {
      if (this.isOnline && !this.syncInProgress) {
        console.log('🚀 Auto-sync executing...');
        try {
          await this.performCompleteSync();
          console.log('✅ Auto-sync completed');
        } catch (error) {
          console.error('❌ Auto-sync failed:', error);
        }
      }
    }, 3000); // 3 seconds delay
  }

  /**
   * Setup online/offline monitoring
   */
  setupOnlineStatusMonitoring() {
    window.addEventListener('online', () => {
      console.log('🌐 Back online - triggering sync...');
      this.isOnline = true;
      this.debounceAutoSync();
    });

    window.addEventListener('offline', () => {
      console.log('📴 Gone offline - sync paused');
      this.isOnline = false;
    });
  }

  /**
   * Stop real-time sync system
   */
  stopRealTimeSync() {
    if (this.autoDownloadInterval) {
      clearInterval(this.autoDownloadInterval);
      this.autoDownloadInterval = null;
    }

    if (this.autoSyncTimeout) {
      clearTimeout(this.autoSyncTimeout);
      this.autoSyncTimeout = null;
    }

    console.log('⏹️ Real-time sync stopped');
  }
}

// Export singleton instance
export default new CloudSyncService(); 